import { formatCnpj } from './cnpj';
import { formatCpf } from './cpf';
import { stripDocumentSeparators } from './internal';

export type DocumentKind = 'cpf' | 'cnpj';

/** CPF (11) ou CNPJ (14) pelo tamanho sem separadores; `null` para qualquer outro tamanho. */
export function documentKind(input: string): DocumentKind | null {
  const length = stripDocumentSeparators(input).length;
  if (length === 11) return 'cpf';
  if (length === 14) return 'cnpj';
  return null;
}

/**
 * CPF ou CNPJ inteiro e formatado para exibição (D10: sem máscara de sigilo), escolhido pelo tamanho.
 * `null` para documento inválido: quem exibe decide se mostra o valor cru.
 */
export function formatDocument(input: string, kind: DocumentKind | 'auto' = 'auto'): string | null {
  const resolved = kind === 'auto' ? documentKind(input) : kind;
  if (resolved === 'cpf') return formatCpf(input);
  if (resolved === 'cnpj') return formatCnpj(input);
  return null;
}

const CPF_INPUT_MASK = '000.000.000-00';
const CNPJ_INPUT_MASK = '00.000.000/0000-00';

/**
 * Valor cru do campo CPF/CNPJ enquanto se digita: letras e dígitos em maiúsculas (CNPJ alfanumérico,
 * IN RFB nº 2.229/2024), até 14; com `alphanumeric: false`, só dígitos (`getDocumentMaskNumeric`).
 */
export function documentInputRaw(value: string, options: { readonly alphanumeric?: boolean } = {}): string {
  const upper = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  return (options.alphanumeric === false ? upper.replace(/\D/g, '') : upper).slice(0, 14);
}

/**
 * Máscara progressiva do legado (`getDocumentMask`, `utils/constants/masks/registrationNumber.ts:79-97`):
 * CPF até 11 caracteres; CNPJ acima disso, com letra ou quando o campo é só de pessoa jurídica.
 */
export function maskDocumentInput(raw: string, options: { readonly legalPerson?: boolean } = {}): string {
  const mask = options.legalPerson === true || raw.length > 11 || /[A-Z]/.test(raw) ? CNPJ_INPUT_MASK : CPF_INPUT_MASK;
  let out = '';
  let index = 0;
  for (const slot of mask) {
    if (index >= raw.length) break;
    if (slot === '0') {
      out += raw.charAt(index);
      index += 1;
    } else {
      out += slot;
    }
  }
  return out;
}

export { stripDocumentSeparators } from './internal';
